import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useNetwork } from 'hooks';
import { API_URL } from 'constant';
import { loginState } from 'global-stores';

/**
 * The Multi Session Bundle drawer's data.
 *
 * Everything here — which keys count as evidence, how strong the bundle is,
 * which session matched on what — is decided by pipelines.api and read
 * verbatim. That is deliberate: "is this the same person" is a compliance
 * judgement, and a second implementation in the client would eventually give a
 * different answer to the same question on the same data.
 */

export type KeyWeight = 'strong' | 'weak' | 'conflict' | 'none' | 'saturated';
export type NameKind = 'exact' | 'near' | 'differs' | 'none';
export type BundleStrength =
	| 'suggested'
	| 'possible'
	| 'probable'
	| 'confirmed'
	| 'conflict';

export interface IKeyEvidence {
	value: string | null;
	shared: number;
	weight: KeyWeight;
	note?: string;
}

export interface IBundleSession {
	_id: string;
	name: string | null;
	email: string | null;
	phone: string | null;
	fund: string | null;
	status: string | null;
	createdAt: string | null;
	updatedAt: string | null;
	anchor: boolean;
	matched: Array<'ssn' | 'id' | 'tel' | 'email'>;
	nameKind: NameKind;
	/** the HUMAN verdict; null means nobody has ruled, which is not the same
	 *  as "reviewed and undecided" */
	reviewed: 'same' | 'different' | null;
	/* The journey, in the same shape the sessions list uses, so the drawer can
	   reuse `toChain` instead of growing its own step ordering. */
	onBoardingName: string | null;
	design: { nodes: Array<{ key?: string; stepId?: string; label?: string }> } | null;
	nodes: Array<{
		_id: string;
		parentId?: string[];
		key?: string;
		stepId?: string;
		visited?: boolean;
		action?: { status?: string };
	}>;
}

/** The four fixed evidence keys, in the order the spine always draws them. */
export interface IBundleKeys {
	ssn: IKeyEvidence;
	id: IKeyEvidence;
	tel: IKeyEvidence;
	email: IKeyEvidence;
}

/** One entry in the bundle's compliance chronology. Append-only. */
export interface ITrailEntry {
	_id: string;
	type: 'note' | 'verdict';
	note: string | null;
	sessionId: string | null;
	verdict: 'same' | 'different' | 'cleared' | null;
	authorName: string;
	createdAt: string;
}

export interface IBundle {
	anchorId: string;
	strength: BundleStrength;
	keys: IBundleKeys;
	name: { kind: NameKind; value: string | null; why?: string };
	sessions: IBundleSession[];
	/** ruled a different person: shown on the shelf, counted nowhere */
	excluded: IBundleSession[];
	/** what the trail attaches to; null when no key is strong enough to name
	 *  this bundle, and the composer is hidden rather than writing entries
	 *  nothing will read back */
	identity: { keyType: string } | null;
	trail: ITrailEntry[];
	total: number;
	capped: boolean;
}

export const useSessionBundle = (sessionId: string | null, open: boolean) => {
	const { get: getBundle, post: postBundle } = useNetwork();
	const userDetail = useRecoilValue(loginState) as any;
	const [posting, setPosting] = useState(false);
	const [bundle, setBundle] = useState<IBundle | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!sessionId) return;
		setLoading(true);
		setError(null);
		try {
			const resp = await getBundle(`${API_URL.SESSION_BUNDLE}/${sessionId}`);
			// useNetwork unwraps {message, data} inconsistently across callers;
			// accept either shape rather than guessing wrong on a rename.
			const payload = resp?.data ?? resp;
			if (payload?.anchorId) setBundle(payload as IBundle);
			else setError('This bundle could not be loaded.');
		} catch {
			setError('This bundle could not be loaded.');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionId]);

	/*
	 * Fetch on OPEN, not on row render. The list can hold hundreds of bundle
	 * rows and each one would otherwise cost a request the moment it painted,
	 * for a drawer nobody has asked for yet.
	 */
	useEffect(() => {
		if (open && sessionId) load();
		if (!open) {
			setBundle(null);
			setError(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, sessionId]);

	/**
	 * Append one entry to the trail, then adopt the bundle the server hands
	 * back. Deliberately not optimistic: a verdict changes which sessions are
	 * in the bundle, what the strength is and what the pill counts, and
	 * guessing all of that on the client is how the drawer would end up
	 * disagreeing with the record it is supposed to be showing.
	 *
	 * `userName`/`userId` go in the body because that is this codebase's
	 * convention for authored records — `activities` does the same. Without
	 * them every entry is written as "Unknown", and an unattributed compliance
	 * trail is not a compliance trail.
	 */
	const post = useCallback(
		async (entry: {
			type: 'note' | 'verdict';
			note?: string;
			targetSessionId?: string;
			verdict?: 'same' | 'different' | 'cleared';
		}) => {
			if (!sessionId) return { ok: false, message: 'no session' };
			setPosting(true);
			try {
				const resp = await postBundle(API_URL.SESSION_BUNDLE, {
					sessionId,
					...entry,
					userName:
						[userDetail?.firstName, userDetail?.lastName]
							.filter(Boolean)
							.join(' ') ||
						userDetail?.name ||
						userDetail?.email,
					userId: userDetail?._id ?? userDetail?.userId,
				});
				const payload = resp?.data ?? resp;
				if (payload?.anchorId) {
					setBundle(payload as IBundle);
					return { ok: true };
				}
				return { ok: false, message: payload?.message ?? 'Could not save.' };
			} catch (e: any) {
				return { ok: false, message: e?.message ?? 'Could not save.' };
			} finally {
				setPosting(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sessionId, userDetail]
	);

	return { bundle, loading, error, reload: load, post, posting };
};

/**
 * Bundle sizes for a whole page of the list, in one request.
 *
 * The pill CANNOT be driven by the session's own `isBundle` flag: that is set
 * when a KYB session spawns representative sessions (several people, one
 * company), which is a different question from this one (one person, several
 * sessions). The two sets barely overlap -- sessions that genuinely share a
 * government ID number carry `isBundle: false` -- so a pill driven by the flag
 * appears on the wrong rows and never on the right ones.
 */
export const useBundleCounts = (ids: string[]) => {
	const { get: getCounts } = useNetwork({ updateState: false });
	const [counts, setCounts] = useState<Record<string, number>>({});
	/* Bumped to force a re-read. A verdict in the drawer changes how many
	   sessions the bundle has, and without this the pill behind it kept its
	   old number — the reviewer's own decision looked like it had done
	   nothing. */
	const [nonce, setNonce] = useState(0);

	// A stable key, so re-renders that do not change the page do not re-fetch.
	const key = ids.join(',');

	useEffect(() => {
		if (!key) {
			setCounts({});
			return;
		}
		let alive = true;
		(async () => {
			try {
				const resp = await getCounts(`${API_URL.SESSION_BUNDLE}?ids=${key}`);
				const rows = resp?.data ?? resp;
				if (!alive || !Array.isArray(rows)) return;
				const next: Record<string, number> = {};
				rows.forEach((r: any) => {
					if (r?.sessionId) next[r.sessionId] = r.others ?? 0;
				});
				setCounts(next);
			} catch {
				/* A pill that cannot be counted is simply not shown. This is a
				   decoration on a working list -- failing it loudly would take the
				   whole sessions page down with it. */
				if (alive) setCounts({});
			}
		})();
		return () => {
			alive = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, nonce]);

	return { counts, refresh: () => setNonce(n => n + 1) };
};
